import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Building2, User } from "lucide-react";
import { Badge, EmptyState, KanbanSkeleton, useToast } from "@/components/ui";
import { api, ApiError } from "@/lib/api";
import { formatRelative } from "@/lib/format";
import { PLAN_COLORS, STAGES, lifetimeTotal, isNearTrial, monthlyValueUsd, computeMeasuredOdds } from "@/features/accounts/platform-meta";

/**
 * The customer funnel as a kanban — every SyncSuite platform account in the
 * column matching its buying-journey stage. Columns are DERIVED from facts
 * (verified, first AI message, publish, domain, card, paid plan), so cards
 * move themselves as customers progress; there's nothing to drag.
 *
 * Rendered as the default tab of the Pipelines page (the opportunity boards
 * remain as the second tab): this CRM's real pipeline IS the platform
 * funnel. Data: GET /platform-accounts (routes/platform-accounts.ts).
 */

interface FunnelAccount {
  id: string;
  slug: string | null;
  name: string | null;
  plan_code: string | null;
  owner_email: string | null;
  owner_name: string | null;
  owner_contact_id?: string | null;
  stage?: string;
  days_in_stage?: number | null;
  email_verified_at?: string | null;
  first_ai_at?: string | null;
  first_published_at?: string | null;
  first_domain_live_at?: string | null;
  first_paid_at?: string | null;
  trial_started_at?: string | null;
  last_active_at: string | null;
  auto_topup_enabled?: boolean | null;
  subscription_status?: string | null;
  trial_ends_at?: string | null;
  revenue_usd?: string | null;
  card_added_at?: string | null;
  lifetime_spend_usd?: string | null;
  wallet_balance_usd?: string | null;
  tags: string[];
}

// Column order/labels come from the shared STAGES list; hints + accents are
// board-specific. "deleted" never renders as a funnel column.
const FUNNEL_EXTRAS: Record<string, { hint: string; accent: string }> = {
  signed_up: { hint: "Created an account", accent: "border-zinc-600" },
  verified: { hint: "Confirmed their email", accent: "border-cyan-500/60" },
  built: { hint: "Sent their first AI message", accent: "border-sky-500/60" },
  published: { hint: "Clicked Publish", accent: "border-indigo-500/60" },
  domain: { hint: "Their domain is connected", accent: "border-blue-500/60" },
  card: { hint: "Payment method on file", accent: "border-emerald-500/60" },
  trialing: { hint: "Paid plan, trial running", accent: "border-amber-500/60" },
  customer: { hint: "On a paid plan", accent: "border-violet-500/60" },
  canceled: { hint: "Paid plan ended", accent: "border-red-500/60" },
};
const COLUMNS = STAGES.filter((st) => st.key !== "deleted" && st.key !== "beta").map((st) => ({
  key: st.key,
  label: st.label,
  hint: FUNNEL_EXTRAS[st.key]?.hint ?? "",
  accent: FUNNEL_EXTRAS[st.key]?.accent ?? "border-zinc-600",
}));

export function AccountFunnelBoard() {
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [accounts, setAccounts] = useState<FunnelAccount[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await api.get<{ accounts: FunnelAccount[] }>("/platform-accounts", {
          limit: 500,
          sort: "last_active",
        });
        if (!cancelled) setAccounts(d.accounts);
      } catch (err) {
        if (!cancelled) toast.error(err instanceof ApiError ? err.message : "Failed to load the funnel");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <KanbanSkeleton />;
  if (accounts.length === 0) {
    return (
      <EmptyState
        icon={Building2}
        title="No platform accounts yet"
        description="Accounts appear here automatically as customers sign up for SyncSuite."
      />
    );
  }

  const byStage = new Map<string, FunnelAccount[]>();
  for (const a of accounts) {
    const key = a.stage ?? "signed_up";
    if (key === "beta" || key === "deleted") continue; // comps are hidden here
    byStage.set(key, [...(byStage.get(key) ?? []), a]);
  }
  // Hide the churn column entirely while it's empty — no dead weight.
  const columns = COLUMNS.filter((c) => c.key !== "canceled" || (byStage.get(c.key)?.length ?? 0) > 0);

  // Money math: real MRR from paying customers + conversion-weighted
  // potential from everyone still in the funnel (model + odds live in
  // platform-meta STAGE_CONVERSION_ODDS — tune there).
  const measuredOdds = computeMeasuredOdds(accounts);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="min-h-0 flex-1 overflow-x-auto pb-2">
        <div className="flex h-full min-w-max gap-3">
        {columns.map((col) => {
          const items = byStage.get(col.key) ?? [];
          const colSpend = items.reduce((n, a) => n + lifetimeTotal(a), 0);
          const colValue = items.reduce((n, a) => n + monthlyValueUsd(a, measuredOdds).usd, 0);
          const colOdds = col.key === "customer" || col.key === "canceled" ? null : measuredOdds[col.key];
          return (
            <div key={col.key} className="flex h-full w-64 shrink-0 flex-col">
              <div className={`mb-2 shrink-0 rounded-lg border-t-2 ${col.accent} bg-zinc-900/50 px-3 py-2`}>
                <div className="flex items-baseline justify-between">
                  <span className="text-sm font-semibold text-zinc-100">{col.label}</span>
                  <span className="text-xs tabular-nums text-zinc-500">
                    {items.length}
                    {colValue > 0 && (
                      <span className={`ml-1.5 ${col.key === "customer" ? "text-emerald-400" : "text-violet-300"}`}>
                        {col.key === "customer" ? `$${colValue.toFixed(0)}/mo` : `≈$${colValue.toFixed(0)}/mo`}
                      </span>
                    )}
                  </span>
                </div>
                <p className="mt-0.5 text-[10px] text-zinc-600">
                  {col.hint}
                  {colOdds != null && ` · ${Math.round(colOdds * 100)}% converted so far`}
                </p>
              </div>
              <div className="min-h-0 flex-1 space-y-2 overflow-y-auto pr-0.5">
                {items.map((a) => (
                  <div
                    key={a.id}
                    onClick={() => navigate(`/accounts/${a.id}`, { state: { from: "/funnel?view=board" } })}
                    className="cursor-pointer rounded-lg border border-zinc-800/80 bg-zinc-900/40 p-2.5 hover:border-indigo-500/40 hover:bg-zinc-900/70"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="min-w-0 truncate text-sm font-medium text-zinc-100">
                        {a.name || a.slug || a.owner_email || "Workspace"}
                      </span>
                      <span className="flex shrink-0 items-center gap-1">
                        {a.plan_code && a.plan_code !== "free" && a.subscription_status === "trialing" && (
                          isNearTrial(a.trial_ends_at)
                            ? <Badge color="amber" size="xs">trial</Badge>
                            : <Badge color="zinc" size="xs">beta</Badge>
                        )}
                        {a.plan_code && (
                          <Badge color={PLAN_COLORS[a.plan_code] ?? "zinc"} size="xs">{a.plan_code}</Badge>
                        )}
                      </span>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-zinc-500">
                      {a.days_in_stage != null && a.days_in_stage > 0 && (
                        <span className="tabular-nums">{a.days_in_stage}d in stage</span>
                      )}
                      {lifetimeTotal(a) > 0 && (
                        <span className="text-emerald-400">${lifetimeTotal(a).toFixed(0)}</span>
                      )}
                      {a.auto_topup_enabled === false && a.card_added_at && (
                        <span className="text-amber-400">top-up off</span>
                      )}
                      {a.last_active_at && <span>{formatRelative(a.last_active_at)}</span>}
                    </div>
                    {/* The human behind the account — one click to their full
                        contact record (inbox, notes, phone, everything). */}
                    {a.owner_email && (
                      a.owner_contact_id ? (
                        <Link
                          to={`/contacts/${a.owner_contact_id}`}
                          state={{ from: location.pathname }}
                          onClick={(e) => e.stopPropagation()}
                          className="mt-1.5 flex items-center gap-1.5 rounded-md border border-zinc-800 bg-zinc-950/60 px-2 py-1 text-[11px] text-indigo-300 hover:border-indigo-500/40 hover:text-indigo-200"
                        >
                          <User className="h-3 w-3 shrink-0" />
                          <span className="min-w-0 truncate">{a.owner_name || a.owner_email}</span>
                        </Link>
                      ) : (
                        <div className="mt-1.5 flex items-center gap-1.5 px-2 py-1 text-[11px] text-zinc-500">
                          <User className="h-3 w-3 shrink-0" />
                          <span className="min-w-0 truncate">{a.owner_email}</span>
                        </div>
                      )
                    )}
                  </div>
                ))}
                {items.length === 0 && (
                  <div className="rounded-lg border border-dashed border-zinc-800/70 p-3 text-center text-[11px] text-zinc-600">
                    Nobody here right now
                  </div>
                )}
              </div>
            </div>
          );
        })}
        </div>
      </div>
    </div>
  );
}
